import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Users, Calendar, Package, CheckCircle2, ChevronLeft, ChevronRight,
  User, Phone, Mail, MapPin, Plus, X, Loader2, AlertCircle, Building2, Sparkles, ChefHat,
} from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
const UNIDADES_INFO = {
  petit: { label: "Celebra Petit", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Building2 },
  kermesita: { label: "La Kermesita", color: "bg-pink-100 text-pink-800 border-pink-300", icon: Sparkles },
  bocatto: { label: "Bocatto", color: "bg-orange-100 text-orange-800 border-orange-300", icon: ChefHat },
};
const TIPOS_EVENTO = ["boda", "xv", "primera comunion", "bautizo", "cumpleanos", "corporativo", "graduacion", "aniversario", "baby shower", "despedida", "otro"];

const WizardNuevoEvento = () => {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [loading, setLoading] = useState(false);

  // Paso 1: cliente
  const [clientes, setClientes] = useState([]);
  const [clienteMode, setClienteMode] = useState("nuevo"); // nuevo | existente
  const [clienteSel, setClienteSel] = useState("");
  const [clienteNuevo, setClienteNuevo] = useState({
    nombre: "", whatsapp: "", email: "", tipo_evento: "boda", nombre_festejado: "",
  });

  // Paso 2: fecha + unidad
  const hoy = new Date();
  const [mes, setMes] = useState(`${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`);
  const [fecha, setFecha] = useState("");
  const [horaInicio, setHoraInicio] = useState("18:00");
  const [horaFin, setHoraFin] = useState("23:00");
  const [unidadesEl, setUnidadesEl] = useState([]); // ["petit","kermesita","bocatto"]
  const [dispoPetit, setDispoPetit] = useState({});

  // Paso 3: paquete / servicios
  const [catalogo, setCatalogo] = useState([]);
  const [itemsPetit, setItemsPetit] = useState([]);
  const [itemsKerm, setItemsKerm] = useState([]);
  const [itemsBocatto, setItemsBocatto] = useState([]);

  // Paso 4: confirmar
  const [adultos, setAdultos] = useState(0);
  const [ninos, setNinos] = useState(0);
  const [ubicacion, setUbicacion] = useState("");
  const [notas, setNotas] = useState("");
  const [anticipo, setAnticipo] = useState(0);
  const [metodo, setMetodo] = useState("transferencia");

  const cargarClientes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clientes`);
      setClientes(data || []);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[Wizard] No se pudo cargar clientes:", err?.message); }
  }, []);
  const cargarCatalogo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/catalogo`).catch(async () => await axios.get(`${API}/servicios`));
      setCatalogo(data || []);
    } catch { setCatalogo([]); }
  }, []);
  const cargarDisponibilidad = useCallback(async () => {
    if (!unidadesEl.includes("petit")) { setDispoPetit({}); return; }
    try {
      const { data } = await axios.get(`${API}/calendario/disponibilidad`, { params: { mes, unidad: "petit" } });
      setDispoPetit(data.dias || {});
    } catch { setDispoPetit({}); }
  }, [mes, unidadesEl]);

  useEffect(() => { cargarClientes(); cargarCatalogo(); }, [cargarClientes, cargarCatalogo]);
  useEffect(() => { cargarDisponibilidad(); }, [cargarDisponibilidad]);

  const diasMes = useMemo(() => {
    const [y, m] = mes.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    const firstDow = new Date(y, m - 1, 1).getDay();
    const arr = Array(firstDow).fill(null);
    for (let d = 1; d <= last; d++) arr.push(`${mes}-${String(d).padStart(2, "0")}`);
    return arr;
  }, [mes]);

  const togUnidad = (u) => setUnidadesEl(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);

  const itemsCatalogoUnidad = (u) => catalogo.filter(c => (c.unidad || c.unidad_negocio || "").toLowerCase() === u || (c.unidades || []).includes(u));

  const agregarItem = (u, item) => {
    const fila = { _rid: `r_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, servicio_id: item.servicio_id || item.id, nombre: item.nombre, cantidad: 1, precio: Number(item.precio) || 0 };
    if (u === "petit") setItemsPetit(prev => [...prev, fila]);
    if (u === "kermesita") setItemsKerm(prev => [...prev, fila]);
    if (u === "bocatto") setItemsBocatto(prev => [...prev, fila]);
  };
  const quitarItem = (u, idx) => {
    if (u === "petit") setItemsPetit(prev => prev.filter((_, i) => i !== idx));
    if (u === "kermesita") setItemsKerm(prev => prev.filter((_, i) => i !== idx));
    if (u === "bocatto") setItemsBocatto(prev => prev.filter((_, i) => i !== idx));
  };
  const updItem = (u, idx, k, v) => {
    const setter = u === "petit" ? setItemsPetit : u === "kermesita" ? setItemsKerm : setItemsBocatto;
    setter(prev => prev.map((it, i) => i === idx ? { ...it, [k]: k === "precio" || k === "cantidad" ? Number(v) || 0 : v } : it));
  };

  const subPetit = itemsPetit.reduce((s, i) => s + i.cantidad * i.precio, 0);
  const subKerm = itemsKerm.reduce((s, i) => s + i.cantidad * i.precio, 0);
  const subBoca = itemsBocatto.reduce((s, i) => s + i.cantidad * i.precio, 0);
  const total = subPetit + subKerm + subBoca;

  const puedeAvanzar = () => {
    if (paso === 1) return clienteMode === "existente" ? !!clienteSel : !!clienteNuevo.nombre.trim();
    if (paso === 2) return !!fecha && unidadesEl.length > 0;
    if (paso === 3) return total > 0;
    return true;
  };

  const confirmar = async () => {
    setLoading(true);
    try {
      const clienteData = clienteMode === "existente"
        ? { cliente_id: clienteSel }
        : { ...clienteNuevo };
      const payload = {
        cliente: clienteData,
        evento: {
          fecha, hora_inicio: horaInicio, hora_fin: horaFin,
          ubicacion, adultos: Number(adultos) || 0, ninos: Number(ninos) || 0,
          notas, tipo: clienteNuevo.tipo_evento || "evento",
          nombre: clienteNuevo.nombre_festejado || (clienteMode === "existente" ? clientes.find(c => c.cliente_id === clienteSel)?.nombre : clienteNuevo.nombre),
        },
        items_petit: itemsPetit,
        items_kermesita: itemsKerm,
        items_bocatto: itemsBocatto,
        origen: "wizard_planner",
      };
      if (anticipo > 0) payload.anticipo = { monto: Number(anticipo), metodo };
      const { data } = await axios.post(`${API}/carrito/checkout`, payload);
      toast.success(anticipo > 0 ? "Evento creado · pago en revisión" : "Evento creado · cotización lista");
      navigate(`/panel/eventos/${data.evento_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el evento");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="wizard-evento">
      {/* Header pasos */}
      <div>
        <h1 className="font-display text-3xl text-stone-900">Nuevo evento</h1>
        <p className="text-stone-500 text-sm">Cuatro pasos. Cero saltos. Listo en menos de 3 minutos.</p>
      </div>
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map(n => (
          <div key={n} className={`flex-1 h-1.5 rounded-full ${paso >= n ? "bg-emerald-600" : "bg-stone-200"}`} />
        ))}
      </div>
      <p className="text-xs text-stone-500">Paso {paso} de 4 · {["Cliente", "Fecha y unidades", "Paquete y servicios", "Confirmar"][paso - 1]}</p>

      {paso === 1 && (
        <Card><CardContent className="p-5 space-y-4">
          <h2 className="font-display text-xl flex items-center gap-2"><Users className="w-5 h-5" /> ¿Quién es el cliente?</h2>
          <div className="flex gap-2">
            <Button variant={clienteMode === "nuevo" ? "default" : "outline"} onClick={() => setClienteMode("nuevo")} data-testid="cli-mode-nuevo">Nuevo</Button>
            <Button variant={clienteMode === "existente" ? "default" : "outline"} onClick={() => setClienteMode("existente")} data-testid="cli-mode-existente">Existente</Button>
          </div>
          {clienteMode === "existente" ? (
            <Select value={clienteSel} onValueChange={setClienteSel}>
              <SelectTrigger data-testid="cli-select"><SelectValue placeholder="Selecciona un cliente..." /></SelectTrigger>
              <SelectContent>
                {clientes.map(c => <SelectItem key={c.cliente_id} value={c.cliente_id}>{c.nombre} {c.whatsapp ? `· ${c.whatsapp}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label><User className="w-3 h-3 inline mr-1" />Nombre completo *</Label>
                <Input value={clienteNuevo.nombre} onChange={(e) => setClienteNuevo({ ...clienteNuevo, nombre: e.target.value })} data-testid="cli-nombre" />
              </div>
              <div><Label><Phone className="w-3 h-3 inline mr-1" />WhatsApp</Label>
                <Input value={clienteNuevo.whatsapp} onChange={(e) => setClienteNuevo({ ...clienteNuevo, whatsapp: e.target.value })} data-testid="cli-whatsapp" />
              </div>
              <div><Label><Mail className="w-3 h-3 inline mr-1" />Email</Label>
                <Input value={clienteNuevo.email} onChange={(e) => setClienteNuevo({ ...clienteNuevo, email: e.target.value })} data-testid="cli-email" />
              </div>
              <div><Label>Tipo de evento</Label>
                <Select value={clienteNuevo.tipo_evento} onValueChange={(v) => setClienteNuevo({ ...clienteNuevo, tipo_evento: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_EVENTO.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>Festejado/a (si aplica)</Label>
                <Input value={clienteNuevo.nombre_festejado} onChange={(e) => setClienteNuevo({ ...clienteNuevo, nombre_festejado: e.target.value })} data-testid="cli-festejado" />
              </div>
            </div>
          )}
        </CardContent></Card>
      )}

      {paso === 2 && (
        <Card><CardContent className="p-5 space-y-4">
          <h2 className="font-display text-xl flex items-center gap-2"><Calendar className="w-5 h-5" /> Fecha y unidades</h2>
          <div>
            <Label className="text-xs uppercase">Unidades de negocio</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {Object.entries(UNIDADES_INFO).map(([k, info]) => {
                const Icon = info.icon; const sel = unidadesEl.includes(k);
                return (
                  <button key={k} onClick={() => togUnidad(k)} className={`p-3 rounded-lg border-2 transition ${sel ? info.color : "bg-white border-stone-200 hover:border-stone-400"}`} data-testid={`unidad-${k}`}>
                    <Icon className="w-5 h-5 mb-1" />
                    <p className="text-sm font-medium">{info.label}</p>
                  </button>
                );
              })}
            </div>
            {unidadesEl.includes("petit") && (
              <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Celebra Petit acepta solo 1 evento por día. El calendario bloquea fechas ocupadas.
              </p>
            )}
          </div>

          {/* Calendario mensual */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Selecciona la fecha</Label>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => {
                  const [y, m] = mes.split("-").map(Number); const d = new Date(y, m - 2, 1);
                  setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                }}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm px-2">{mes}</span>
                <Button size="sm" variant="outline" onClick={() => {
                  const [y, m] = mes.split("-").map(Number); const d = new Date(y, m, 1);
                  setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                }}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase text-stone-400 mb-1">
              {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {diasMes.map((d, i) => {
                if (!d) return <div key={`wiz-empty-${i}`} />;
                const dia = d.split("-")[2];
                const ocup = dispoPetit[d];
                const esPasado = d < `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
                const bloqueado = ocup && (ocup.estado === "ocupado" || ocup.estado === "bloqueado" || ocup.estado === "multiples");
                const seleccionado = d === fecha;
                let cls = "border-stone-200 bg-white text-stone-900";
                if (esPasado) cls = "border-stone-100 bg-stone-50 text-stone-300 cursor-not-allowed";
                else if (bloqueado) cls = "border-red-300 bg-red-50 text-red-700 cursor-not-allowed";
                else cls += " hover:bg-emerald-50 cursor-pointer";
                if (seleccionado) cls = "border-emerald-600 bg-emerald-600 text-white";
                return (
                  <button key={d}
                    disabled={esPasado || bloqueado}
                    onClick={() => setFecha(d)}
                    title={bloqueado ? `Ocupado: ${(ocup.eventos || []).map(e => e.nombre).join(", ") || ocup.motivo || "bloqueado"}` : ""}
                    className={`aspect-square rounded border text-xs font-medium ${cls}`}
                    data-testid={`dia-${d}`}>
                    {dia}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3 mt-2 text-[10px] text-stone-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border bg-white" /> Libre</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-300" /> Ocupado / bloqueado</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600" /> Seleccionado</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Hora inicio</Label><Input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} data-testid="evt-inicio" /></div>
            <div><Label>Hora fin</Label><Input type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} data-testid="evt-fin" /></div>
          </div>
        </CardContent></Card>
      )}

      {paso === 3 && (
        <Card><CardContent className="p-5 space-y-4">
          <h2 className="font-display text-xl flex items-center gap-2"><Package className="w-5 h-5" /> Paquete y servicios</h2>
          {unidadesEl.length === 0 && <p className="text-amber-700 text-sm">Selecciona al menos una unidad en el paso 2.</p>}
          {unidadesEl.map(u => {
            const info = UNIDADES_INFO[u];
            const items = u === "petit" ? itemsPetit : u === "kermesita" ? itemsKerm : itemsBocatto;
            const sub = items.reduce((s, i) => s + i.cantidad * i.precio, 0);
            const catUnidad = itemsCatalogoUnidad(u);
            return (
              <div key={u} className={`rounded-lg border-2 ${info.color} p-3`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{info.label}</p>
                  <p className="font-display text-lg">{fmt(sub)}</p>
                </div>
                {catUnidad.length > 0 && (
                  <Select onValueChange={(v) => { const it = catUnidad.find(c => (c.servicio_id || c.id) === v); if (it) agregarItem(u, it); }}>
                    <SelectTrigger className="mb-2 bg-white" data-testid={`add-${u}`}><SelectValue placeholder="Agregar del catálogo..." /></SelectTrigger>
                    <SelectContent>
                      {catUnidad.map(c => <SelectItem key={c.servicio_id || c.id} value={c.servicio_id || c.id}>{c.nombre} · {fmt(c.precio)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {items.map((it, i) => (
                  <div key={it._rid || `${u}-fixed-${i}`} className="flex items-center gap-2 bg-white rounded p-2 mb-1">
                    <Input value={it.nombre} onChange={(e) => updItem(u, i, "nombre", e.target.value)} className="flex-1" data-testid={`item-nombre-${u}-${i}`} />
                    <Input type="number" value={it.cantidad} onChange={(e) => updItem(u, i, "cantidad", e.target.value)} className="w-16" data-testid={`item-qty-${u}-${i}`} />
                    <span className="text-xs text-stone-500">×</span>
                    <Input type="number" value={it.precio} onChange={(e) => updItem(u, i, "precio", e.target.value)} className="w-24" data-testid={`item-precio-${u}-${i}`} />
                    <Button size="sm" variant="ghost" onClick={() => quitarItem(u, i)} data-testid={`item-del-${u}-${i}`}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => agregarItem(u, { nombre: "", precio: 0 })} data-testid={`btn-linea-manual-${u}`}>
                  <Plus className="w-3 h-3 mr-1" /> Línea manual
                </Button>
              </div>
            );
          })}
          <div className="border-t pt-3 flex justify-between">
            <span className="text-stone-600">Total</span>
            <span className="font-display text-2xl">{fmt(total)}</span>
          </div>
        </CardContent></Card>
      )}

      {paso === 4 && (
        <Card><CardContent className="p-5 space-y-4">
          <h2 className="font-display text-xl flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Confirmar y crear</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Adultos</Label><Input type="number" value={adultos} onChange={(e) => setAdultos(e.target.value)} /></div>
            <div><Label>Niños</Label><Input type="number" value={ninos} onChange={(e) => setNinos(e.target.value)} /></div>
            <div className="col-span-2"><Label><MapPin className="w-3 h-3 inline mr-1" />Ubicación</Label><Input value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} /></div>
            <div className="col-span-2"><Label>Notas</Label><Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} /></div>
            <div><Label>Anticipo (opcional)</Label><Input type="number" value={anticipo} onChange={(e) => setAnticipo(e.target.value)} /></div>
            <div><Label>Método</Label>
              <Select value={metodo} onValueChange={setMetodo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {anticipo > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 inline mr-1" /> El pago quedará <strong>pendiente de aprobación</strong> en /panel/hoy hasta que tú lo valides. El evento se queda como &quot;cotizado&quot;.
            </div>
          )}
          <div className="bg-stone-50 rounded p-3 space-y-1">
            <p className="text-sm flex justify-between"><span>Fecha:</span><span className="font-medium">{fecha} · {horaInicio} a {horaFin}</span></p>
            <p className="text-sm flex justify-between"><span>Unidades:</span><span>{unidadesEl.map(u => UNIDADES_INFO[u].label).join(", ")}</span></p>
            <p className="text-sm flex justify-between border-t pt-1"><span>Total:</span><span className="font-display text-xl">{fmt(total)}</span></p>
            {anticipo > 0 && <p className="text-sm flex justify-between"><span>Anticipo:</span><span>{fmt(anticipo)} ({metodo})</span></p>}
          </div>
        </CardContent></Card>
      )}

      {/* Navegación */}
      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={paso === 1} onClick={() => setPaso(p => p - 1)} data-testid="btn-prev">
          <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
        </Button>
        {paso < 4 ? (
          <Button onClick={() => setPaso(p => p + 1)} disabled={!puedeAvanzar()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-next">
            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={confirmar} disabled={loading} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-confirmar">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Crear evento
          </Button>
        )}
      </div>
    </div>
  );
};

export default WizardNuevoEvento;
